"use client"

import { Box, Flex, type FlexProps } from "@chakra-ui/react"
import { type FC, type ReactNode } from "react"

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const EASE_STANDARD = "cubic-bezier(0.4, 0, 0.2, 1)"
const EASE_OVERSHOOT = "cubic-bezier(0.34, 1.2, 0.64, 1)"

/** Backdrop filter applied to the frosted surface. */
const FROSTED_BACKDROP = "blur(24px) saturate(200%) brightness(110%)"

/** Per-property transition string for the glass shell. */
const SHELL_TRANSITION = [
  `padding 500ms ${EASE_STANDARD}`,
  `gap 500ms ${EASE_STANDARD}`,
  `max-width 500ms ${EASE_STANDARD}`,
  `background-color 500ms ${EASE_STANDARD}`,
  `backdrop-filter 500ms ${EASE_STANDARD}`,
  `-webkit-backdrop-filter 500ms ${EASE_STANDARD}`,
  `border-color 500ms ${EASE_STANDARD}`,
  `box-shadow 500ms ${EASE_STANDARD}`,
  `border-radius 600ms ${EASE_OVERSHOOT}`,
].join(", ")

const OVERLAY_TRANSITION = `opacity 500ms ${EASE_STANDARD}`

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface LiquidGlassProps extends FlexProps {
  /**
   * Toggles the frosted glass effect on/off. When `false`, falls back to a
   * flat solid surface (useful for animated transitions). Defaults to `true`.
   */
  active?: boolean
  /**
   * Children rendered above the overlay layers.
   */
  children?: ReactNode
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

/**
 * Reusable frosted "liquid glass" surface. Renders a relatively-positioned
 * container with two stacked gradient overlays, a backdrop blur, and a
 * theme-aware border / shadow. Light and dark mode are driven by the
 * `navbar.*` semantic tokens defined in `src/theme/semantic-tokens`.
 *
 * Consumers position their content as children — overlays sit underneath
 * (`zIndex: 0`) and content should set `position="relative"` + `zIndex={1}`
 * if it must paint above them.
 */
const LiquidGlass: FC<LiquidGlassProps> = ({
  active = true,
  borderRadius = "100px",
  children,
  ...rest
}) => {
  return (
    <Flex
      backdropFilter={active ? FROSTED_BACKDROP : "none"}
      bgColor={active ? "navbar.glass_bg" : "navbar.solid_bg"}
      borderColor={active ? "navbar.glass_border" : "border"}
      borderRadius={active ? borderRadius : "0px"}
      borderStyle="solid"
      borderWidth="1px"
      boxShadow={active ? "navbar.glass" : "navbar.flat"}
      css={{
        transition: SHELL_TRANSITION,
        WebkitBackdropFilter: active ? FROSTED_BACKDROP : "none",
      }}
      position="relative"
      {...rest}
    >
      {/* Liquid-glass overlay layers */}
      <Box
        borderRadius="inherit"
        css={{
          _dark: {
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 40%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.04) 100%)",
          },
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.1) 40%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.15) 100%)",
          transition: OVERLAY_TRANSITION,
        }}
        inset={0}
        opacity={active ? 1 : 0}
        pointerEvents="none"
        position="absolute"
        zIndex={0}
      />
      <Box
        borderRadius="inherit"
        css={{
          _dark: {
            background:
              "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(255,255,255,0.06) 0%, transparent 70%)",
          },
          background:
            "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(255,255,255,0.4) 0%, transparent 70%)",
          transition: OVERLAY_TRANSITION,
        }}
        inset={0}
        opacity={active ? 1 : 0}
        pointerEvents="none"
        position="absolute"
        zIndex={0}
      />
      {children}
    </Flex>
  )
}

export default LiquidGlass
